import React, { useState, useEffect } from 'react';

interface VisionDeckProps {
  onExit: () => void;
}

interface Slide {
  id: string;
  title: string;
  highlight: string;
  description: string;
  points: string[];
  image?: string;
  videoUrl?: string;
  showMobileMockup?: boolean;
}

const LOGO_SVG = (
  <div className="flex items-center gap-3">
    <div className="flex flex-col leading-none">
      <span className="text-xl font-black tracking-tighter text-white uppercase leading-[0.8]">
        NEOPAWN
      </span>
      <span className="text-[10px] font-bold tracking-[0.4em] text-white/50 uppercase mt-1">
        .com
      </span>
    </div>
  </div>
);

const slides: Slide[] = [
  {
    id: 'slide-1',
    title: 'NEOPAWN.COM',
    highlight: 'Infrastructure White Label for Pawns ON CHAIN. Plug and Play.',
    description: 'Welcome to the future of institutional pawn credit. A secure, audited, and ready-to-deploy infrastructure designed for the next generation of financial gateways.',
    points: ['Ready to Audit', 'Institutional Grade'],
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop',
  },
  {
    id: 'slide-2',
    title: 'Unlocking Trillions',
    highlight: 'STAGNANT CAPITAL',
    description: 'The global economy is losing trillions in potential value. Because of the friction in requesting loans, massive capital remains dormant, slowing human progress. We bridge this gap.',
    points: ['Efficiency Engine', 'Accelerated Growth'],
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop',
    showMobileMockup: true
  },
  {
    id: 'slide-3',
    title: 'The Solution for Banks',
    highlight: 'PLUG & PLAY',
    description: 'NeoPawn is a white-label infrastructure designed for banks to activate dormant capital. It allows institutions to offer digital credit under their own brand instantly.',
    points: ['Legacy Integration', 'Awakened Capital'],
    image: 'https://images.unsplash.com/photo-1554469384-e58fac16e23a?q=80&w=2070&auto=format&fit=crop',
  },
  {
    id: 'slide-4',
    title: 'NeoPawn Ecosystem',
    highlight: 'DEMO',
    description: 'Experience the institutional terminal. A seamless bridge between traditional compliance and decentralized liquidity rails.',
    points: ['Cinema Experience', 'Live Interface'],
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070&auto=format&fit=crop',
  },
  {
    id: 'slide-5',
    title: 'Business Model',
    highlight: 'PAY AS YOU GROW',
    description: 'FreeMium model scaling with white-label wallet count. Custom loan pools attract a $25K annual fee. 0.2% commission on key movements: Deposit and Borrow.',
    points: ['Sustainable Scaling', 'Institutional Pools'],
    image: 'https://images.unsplash.com/photo-1611974714851-eb605161ca81?q=80&w=2070&auto=format&fit=crop',
  },
  {
    id: 'slide-6',
    title: 'Market Strategy',
    highlight: 'TAM: $2.1T (2027)',
    description: 'The global tokenized credit market is projected to reach $2.1 Trillion by 2027. We are observing growing "crypto-loan" symptoms with TVL in institutional pools increasing exponentially as banks seek MiCA-compliant rails.',
    points: ['TAM: $2.1T Global', 'SAM: EU MiCA Banks', 'SOM: NeoPawn Node Capture'],
    image: 'https://images.unsplash.com/photo-1642104704074-907c0698cbd9?q=80&w=2070&auto=format&fit=crop',
  },
  {
    id: 'slide-7',
    title: 'Investment Goal',
    highlight: 'USD 7.5M FOR 10%',
    description: 'Seeking a $7.5M Pre-Seed round to scale our institutional solution. We are building the foundational rails to capture and expand the early-stage TAM.',
    points: ['Pre-Seed Round', 'Global Expansion'],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2026&auto=format&fit=crop',
  },
  {
    id: 'slide-8',
    title: 'Thank You',
    highlight: 'LEGACY READY',
    description: 'Join us in awakening the world\'s stagnant capital and redefining the future of institutional credit. NeoPawn.com',
    points: ['Global Infrastructure', 'Future of Finance'],
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2069&auto=format&fit=crop',
  }
];

const GaliciaMockup: React.FC = () => (
  <div className="relative w-full h-full flex items-center justify-center p-8 sm:p-12 animate-fade-in overflow-hidden">
    <div className="w-[240px] h-[500px] mt-24 sm:mt-32 bg-black border-[6px] border-[#1a1a1a] rounded-[40px] shadow-[0_0_120px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col relative scale-90 sm:scale-100">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-[#1a1a1a] rounded-b-2xl z-20"></div>
      
      <div className="pt-10 pb-4 px-6 flex justify-between items-center bg-black border-b border-white/5">
        <div className="flex flex-col">
          <span className="text-[11px] font-black tracking-tighter text-white uppercase italic">GALICIA</span>
          <span className="text-[6px] font-bold text-white/30 tracking-widest uppercase -mt-1">Private Banking</span>
        </div>
        <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-6 space-y-6 no-scrollbar">
        <div>
          <p className="text-[8px] font-bold text-white/30 uppercase tracking-widest mb-1">Available Liquidity</p>
          <h4 className="text-xl font-black text-white tracking-tighter">$1,240,500.00</h4>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h5 className="text-[9px] font-black text-white uppercase tracking-widest">Active RWA Pools</h5>
            <span className="text-[7px] font-bold text-orange-500 uppercase">New +</span>
          </div>
          
          {[
            { name: 'Grain Export Pool', cap: '5M', yield: '8.2%' },
            { name: 'Real Estate Dev', cap: '12M', yield: '6.5%' },
            { name: 'SME Credit Line', cap: '3.2M', yield: '9.0%' }
          ].map((pool, idx) => (
            <div key={idx} className="bg-white/[0.03] border border-white/10 p-3 rounded-xl space-y-1.5">
              <div className="flex justify-between items-center">
                <span className="text-[8px] font-bold text-white uppercase truncate pr-2">{pool.name}</span>
                <span className="text-[8px] font-black text-white/40 italic">{pool.yield}</span>
              </div>
              <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-white/40 w-3/4"></div>
              </div>
              <p className="text-[6px] font-bold text-white/20 uppercase tracking-widest">CAP: {pool.cap} USD</p>
            </div>
          ))}
        </div>

        <button className="w-full bg-white text-black py-3 rounded-xl text-center group active:scale-95 transition-transform">
          <p className="text-[8px] font-black uppercase tracking-[0.2em]">Deploy Node</p>
        </button>
      </div>

      <div className="h-14 bg-black border-t border-white/5 flex justify-around items-center px-4">
        <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
        <div className="w-1.2 h-1.2 rounded-full bg-white/20"></div>
        <div className="w-1.2 h-1.2 rounded-full bg-white/20"></div>
        <div className="w-1.2 h-1.2 rounded-full bg-white/20"></div>
      </div>
    </div>
  </div>
);

const VisionDeck: React.FC<VisionDeckProps> = ({ onExit }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'Escape') onExit();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const slide = slides[currentSlide];
  const isIntroSlide = currentSlide === 0;

  return (
    <div className="fixed inset-0 z-[200] flex flex-col lg:flex-row bg-black overflow-hidden font-sans">
      <button 
        onClick={onExit}
        className="absolute top-6 right-6 lg:top-10 lg:right-10 z-[210] text-white/40 hover:text-white transition-colors bg-white/5 hover:bg-white/10 p-3 rounded-full"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Main Content Area */}
      <div 
        className="flex flex-col justify-center bg-black relative animate-fade-in transition-all duration-700 w-full lg:w-1/2 px-6 sm:px-12 lg:px-24 py-12 lg:py-0 order-2 lg:order-1"
        key={`left-${currentSlide}`}
      >
        <div className="w-full max-w-xl mx-auto lg:mx-0">
          <div className="mb-10 sm:mb-20 hidden lg:block scale-125 origin-left">
            {LOGO_SVG}
          </div>
          
          <div className="mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight tracking-tighter uppercase">
              {slide.title}
            </h1>
            <h2 className="text-xl sm:text-2xl lg:text-3xl text-white/40 italic font-bold uppercase py-4 border-y border-white/5 w-full">
              {slide.highlight}
            </h2>
          </div>
          
          {slide.description && (
            <p className="text-base sm:text-xl text-white/40 mb-10 sm:mb-14 leading-relaxed font-medium max-w-lg">
              {slide.description}
            </p>
          )}
          
          {/* Points column (Hidden for Slide 6 as they move to the right) */}
          {slide.id !== 'slide-6' && slide.points.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-16">
              {slide.points.map((point, i) => (
                <div key={i} className="flex flex-col gap-1 p-5 bg-white/[0.03] border border-white/10 rounded-sm group hover:border-white/20 transition-all text-left">
                  <div className="flex items-center gap-2 mb-1 text-left">
                    <div className="w-1 h-1 bg-white rounded-full shadow-[0_0_12px_rgba(255,255,255,0.9)]" />
                    <span className="text-[9px] font-black text-white uppercase tracking-[0.3em]">{point}</span>
                  </div>
                  <span className="text-[8px] font-bold text-white/20 uppercase tracking-widest">Verified Infrastructure</span>
                </div>
              ))}
            </div>
          )}

          {isIntroSlide && (
            <div className="mt-8">
              <button
                onClick={nextSlide}
                className="bg-white text-black px-12 py-5 font-black uppercase tracking-[0.3em] text-xs hover:bg-white/90 active:scale-95 transition-all shadow-2xl flex items-center gap-4"
              >
                Start Presentation
                <span className="text-lg">→</span>
              </button>
            </div>
          )}

          <div className="flex items-center gap-4 lg:hidden mt-12">
            <button onClick={prevSlide} className="flex-1 bg-white/5 border border-white/10 text-white font-bold py-4 rounded-sm text-xs uppercase tracking-widest">Prev</button>
            <button onClick={nextSlide} className="flex-1 bg-white text-black font-bold py-4 rounded-sm text-xs uppercase tracking-widest">Next</button>
          </div>
        </div>
        
        {/* Progress Navigation */}
        <div className="absolute bottom-12 hidden lg:flex items-center gap-8 left-12 lg:left-24">
          <div className="flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`h-0.5 transition-all duration-500 ${i === currentSlide ? 'w-12 bg-white' : 'w-4 bg-white/10'}`}
              />
            ))}
          </div>
          <span className="text-[10px] font-black text-white/20 uppercase tracking-[0.4em]">
            MANIFESTO_NEOPAWN_0{currentSlide + 1}
          </span>
        </div>
      </div>

      {/* Right Image/Video Section */}
      <div className="w-full lg:w-1/2 h-64 sm:h-80 lg:h-full relative overflow-hidden order-1 lg:order-2 bg-[#050505]" key={`right-${currentSlide}`}>
        {/* Always render background image as backdrop/fallback */}
        <div className="absolute inset-0">
          <img 
            src={slide.image}
            alt="Visual Backdrop"
            className="w-full h-full object-cover brightness-[0.35] grayscale contrast-125 scale-105 transition-transform duration-[3s]"
          />
        </div>

        {slide.videoUrl ? (
          <div className="relative w-full h-full flex items-center justify-center p-4 lg:p-12 z-10 backdrop-blur-[2px]">
            <div className="w-full aspect-video bg-black/80 border border-white/10 shadow-[0_0_80px_rgba(0,0,0,1)] overflow-hidden relative group rounded-sm">
              <iframe
                src={slide.videoUrl}
                className="w-full h-full grayscale hover:grayscale-0 transition-all duration-1000"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
              <div className="absolute inset-0 pointer-events-none border border-white/5"></div>
            </div>
          </div>
        ) : (
          <div className="w-full h-full relative z-10">
            {/* Slide 6: Market Strategy Points overlay */}
            {slide.id === 'slide-6' && (
              <div className="absolute inset-0 flex items-center justify-center p-12 backdrop-blur-[1px]">
                <div className="flex flex-col gap-6 w-full max-w-sm">
                  {slide.points.map((point, i) => (
                    <div key={i} className="flex flex-col gap-2 p-6 bg-black/85 backdrop-blur-xl border border-white/15 rounded-sm animate-fade-in shadow-[0_30px_60px_-15px_rgba(0,0,0,0.8)]" style={{animationDelay: `${i * 200}ms`}}>
                      <div className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 bg-white rounded-full shadow-[0_0_15px_rgba(255,255,255,1)]" />
                        <span className="text-xs font-black text-white uppercase tracking-[0.4em]">{point}</span>
                      </div>
                      <div className="h-0.5 w-full bg-white/5 mt-1 overflow-hidden">
                        <div className="h-full bg-white/30 w-1/3 animate-marquee-slow" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {slide.showMobileMockup && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <GaliciaMockup />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/20 lg:bg-none"></div>
          </div>
        )}
        
        <div className="absolute bottom-12 right-12 z-20 hidden lg:flex gap-4">
          <button onClick={prevSlide} className="w-12 h-12 flex items-center justify-center bg-black/50 backdrop-blur-md border border-white/20 text-white hover:bg-white hover:text-black transition-all rounded-full group">
            <span className="text-lg">←</span>
          </button>
          <button onClick={nextSlide} className="w-12 h-12 flex items-center justify-center bg-white text-black hover:bg-black hover:text-white border border-white transition-all rounded-full shadow-2xl group">
            <span className="text-lg">→</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default VisionDeck;
